import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import Gallery from "@/components/Gallery";
import {
  Phone,
  Scissors,
  Axe,
  TriangleAlert,
  Trees,
  Trash2,
  ShieldCheck,
  Clock4,
  MapPin,
  Star,
  CheckCircle2,
  Send,
  Loader2,
  Mail,
  MessageSquare,
  ArrowRight,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PHONE = "1-865-621-0995";
const PHONE_TEL = "+18656210995";
const PHONE_DISPLAY = "(865) 621-0995";
const SMS_BODY =
  "Hi Acorn Tree Service! I'd like a free quote. Here's a photo of the tree and my address:";
const SMS_LINK = `sms:${PHONE_TEL}?&body=${encodeURIComponent(SMS_BODY)}`;

const SERVICES = [
  {
    id: "tree-trimming",
    title: "Tree Trimming",
    icon: Scissors,
    blurb: "Shape, prune, and lift the canopy. Healthier trees, cleaner yards, more sunlight.",
    image:
      "https://images.unsplash.com/photo-1754321902809-5c21cbc67228?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxhcmJvcmlzdCUyMHRyZWUlMjBzZXJ2aWNlfGVufDB8fHx8MTc3ODEyODEzOXww&ixlib=rb-4.1.0&q=85",
    span: false,
  },
  {
    id: "dangerous-removal",
    title: "Dangerous Tree Removal",
    icon: TriangleAlert,
    blurb:
      "Leaning over the house? Split trunk after the storm? We rope it, drop it, and haul it out — safely.",
    image:
      "https://images.unsplash.com/photo-1699391414255-3a10698f8d50?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwxfHxjaGFpbnNhdyUyMGN1dHRpbmclMjB0cmVlfGVufDB8fHx8MTc3ODEyODEzOXww&ixlib=rb-4.1.0&q=85",
    span: true,
  },
  {
    id: "tree-removal",
    title: "Tree Removal",
    icon: Axe,
    blurb: "Dead, dying, or just plain ugly — we take it to the ground and clean up after.",
    image:
      "https://images.unsplash.com/photo-1765064518620-e9e78c41a687?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwzfHxhcmJvcmlzdCUyMHRyZWUlMjBzZXJ2aWNlfGVufDB8fHx8MTc3ODEyODEzOXww&ixlib=rb-4.1.0&q=85",
    span: false,
  },
  {
    id: "lot-clearing",
    title: "Lot Clearing",
    icon: Trees,
    blurb: "Wooded lot to buildable lot. Brush, saplings, stumps, and full timber clears.",
    image:
      "https://images.unsplash.com/photo-1720370494375-bd67eadc5c79?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwyfHx3b29kJTIwZGVicmlzJTIwY2xlYXJpbmd8ZW58MHx8fHwxNzc4MTI4MTM5fDA&ixlib=rb-4.1.0&q=85",
    span: false,
  },
  {
    id: "debris-removal",
    title: "Debris Removal",
    icon: Trash2,
    blurb: "Storm cleanup, fallen limbs, brush piles. Hauled off and gone the same day.",
    image:
      "https://images.unsplash.com/photo-1720370494375-bd67eadc5c79?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwyfHx3b29kJTIwZGVicmlzJTIwY2xlYXJpbmd8ZW58MHx8fHwxNzc4MTI4MTM5fDA&ixlib=rb-4.1.0&q=85",
    span: false,
  },
];

const SERVICE_AREAS = [
  "Kingston",
  "Oak Ridge",
  "Knoxville",
  "Harriman",
  "Lenoir City",
  "Loudon",
  "Rockwood",
  "Farragut",
  "Crossville",
  "Sweetwater",
  "Spring City",
  "Oliver Springs",
  "Athens",
  "Chattanooga",
];

const TRUST = [
  { icon: ShieldCheck, label: "Fully Insured" },
  { icon: Clock4, label: "24/7 Storm Response" },
  { icon: CheckCircle2, label: "Free Estimates" },
  { icon: MapPin, label: "Locally Owned" },
];

const TESTIMONIALS = [
  {
    name: "Brenda H.",
    town: "Kingston, TN",
    text:
      "Massive oak split right over our roof in the storm. They were on-site in two hours, had it down by dark. Cleaned up the yard like they were never there.",
  },
  {
    name: "Mike R.",
    town: "Harriman, TN",
    text:
      "Cleared a half-acre lot for me — every brush pile, every stump. Fair price and they actually showed up when they said they would.",
  },
  {
    name: "Sarah P.",
    town: "Oak Ridge, TN",
    text:
      "Three dead pines next to the driveway. Quick quote, clean cut, hauled it all off. Real professional folks.",
  },
];

export default function Landing({ scrollTo }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    service_type: "Tree Trimming",
    description: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!scrollTo) return;
    const t = setTimeout(() => {
      const el = document.getElementById(scrollTo);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 300);
    return () => clearTimeout(t);
  }, [scrollTo]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.address) {
      toast.error("Name, phone, and address are required.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      const { data } = await axios.post(`${API}/quote`, payload);
      toast.success("Got it! We'll call you back shortly.", {
        description: data.email_sent ? "Estimate request sent." : "Estimate request received.",
      });
      setForm({
        name: "",
        phone: "",
        email: "",
        address: "",
        service_type: "Tree Trimming",
        description: "",
      });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please call us instead.";
      toast.error(typeof msg === "string" ? msg : "Could not submit. Please call us.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="font-body bg-bone text-ink min-h-screen">
      {/* ======= TOP BANNER ======= */}
      <div
        data-testid="top-banner"
        className="bg-safety text-white border-b-2 border-forest"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-2 flex flex-wrap items-center justify-center gap-x-6 gap-y-1 text-center">
          <span className="font-display tracking-[0.2em] text-base sm:text-lg flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" strokeWidth={3} /> FREE ESTIMATES
          </span>
          <span className="hidden sm:inline text-white/60">·</span>
          <span className="font-display tracking-[0.2em] text-base sm:text-lg flex items-center gap-2">
            <Clock4 className="w-4 h-4" strokeWidth={3} /> 24-HOUR STORM WORK
          </span>
          <span className="hidden md:inline text-white/60">·</span>
          <a
            href={`tel:${PHONE_TEL}`}
            data-testid="banner-call"
            className="hidden md:inline-flex font-display tracking-[0.2em] text-base sm:text-lg items-center gap-2 hover:underline"
          >
            <Phone className="w-4 h-4" strokeWidth={3} /> {PHONE_DISPLAY}
          </a>
        </div>
      </div>

      {/* ======= NAV ======= */}
      <nav
        data-testid="site-nav"
        className="sticky top-0 z-50 bg-forest text-bone border-b-2 border-forest"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-3 flex items-center justify-between">
          <a href="#top" data-testid="brand-link" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-safety border-2 border-bone flex items-center justify-center">
              <Trees className="w-5 h-5 text-bone" strokeWidth={2.5} />
            </div>
            <div className="leading-none">
              <div className="font-display text-2xl tracking-wider">ACORN TREE<sup className="text-safety text-sm ml-0.5">2</sup></div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-bone/70">
                Service · Kingston, TN
              </div>
            </div>
          </a>
          <div className="hidden md:flex items-center gap-8 font-display text-lg tracking-widest">
            <a href="#services" className="hover:text-safety transition-colors" data-testid="nav-services">SERVICES</a>
            <a href="#area" className="hover:text-safety transition-colors" data-testid="nav-area">SERVICE AREA</a>
            <a href="#why" className="hover:text-safety transition-colors" data-testid="nav-why">WHY US</a>
            <a href="#quote" className="hover:text-safety transition-colors" data-testid="nav-quote">QUOTE</a>
          </div>
          <a
            href={`tel:${PHONE_TEL}`}
            data-testid="nav-call-button"
            className="btn-primary px-4 py-2 text-base sm:text-lg flex items-center gap-2"
          >
            <Phone className="w-4 h-4" strokeWidth={3} />
            <span className="hidden sm:inline">{PHONE_DISPLAY}</span>
            <span className="sm:hidden">CALL</span>
          </a>
        </div>
      </nav>

      {/* ======= HERO ======= */}
      <section id="top" className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1765064518620-e9e78c41a687?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwzfHxhcmJvcmlzdCUyMHRyZWUlMjBzZXJ2aWNlfGVufDB8fHx8MTc3ODEyODEzOXww&ixlib=rb-4.1.0&q=85)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/30" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-20 sm:py-28 lg:py-36 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8 text-bone">
            <div className="inline-flex items-center gap-2 bg-safety text-white px-3 py-1 border-2 border-bone font-display tracking-widest text-sm rise">
              <span className="w-2 h-2 bg-bone block animate-pulse"></span>
              SERVING KINGSTON, TN + 100 MILES
            </div>
            <h1
              data-testid="hero-headline"
              className="font-display uppercase tracking-tight text-[64px] leading-[0.9] sm:text-7xl lg:text-[112px] mt-6 rise rise-d1"
            >
              Dangerous Tree?
              <br />
              <span className="text-safety">Ugly Tree?</span>
              <br />
              We Take Care Of It.
            </h1>
            <p className="mt-6 max-w-2xl text-lg sm:text-xl text-bone/90 rise rise-d2">
              Trimming, removal, lot clearing, and debris haul-off across East Tennessee.
              Climbed, cut, and cleaned up by folks who know what they're doing.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-4 rise rise-d3">
              <a
                href={`tel:${PHONE_TEL}`}
                data-testid="hero-call-button"
                className="btn-primary px-7 py-5 text-2xl flex items-center gap-3"
              >
                <Phone className="w-6 h-6" strokeWidth={3} />
                CALL {PHONE_DISPLAY}
              </a>
              <a
                href="#quote"
                data-testid="hero-quote-button"
                className="btn-dark px-7 py-5 text-2xl flex items-center gap-3"
              >
                GET A FREE QUOTE
                <ArrowRight className="w-6 h-6" strokeWidth={3} />
              </a>
              <a
                href={SMS_LINK}
                data-testid="hero-text-photo-button"
                className="btn-dark px-7 py-5 text-2xl flex items-center gap-3 bg-bone text-forest hover:bg-white"
                style={{ background: "#F4F1ED", color: "#1B2A22", boxShadow: "4px 4px 0 0 #E65C00" }}
              >
                <MessageSquare className="w-6 h-6" strokeWidth={3} />
                TEXT US A PHOTO
              </a>
            </div>

            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm uppercase tracking-widest text-bone/85 rise rise-d4">
              {TRUST.map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-center gap-2" data-testid={`trust-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                  <Icon className="w-4 h-4 text-safety" strokeWidth={2.5} />
                  <span>{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Phone card */}
          <div className="lg:col-span-4 rise rise-d3">
            <div className="bg-bone text-ink border-2 border-forest hard-shadow-lg p-6">
              <div className="text-xs uppercase tracking-[0.25em] text-forest/70 font-bold">
                24/7 Storm Line
              </div>
              <a
                href={`tel:${PHONE_TEL}`}
                data-testid="hero-card-phone"
                className="block font-display text-5xl sm:text-6xl text-forest leading-none mt-2 hover:text-safety transition-colors"
              >
                {PHONE_DISPLAY}
              </a>
              <div className="h-1 bg-safety w-16 my-5"></div>
              <ul className="space-y-2 text-sm">
                {[
                  "Free on-site estimates",
                  "Trees on house? We're rolling.",
                  "Stumps, brush & cleanup included",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-safety mt-0.5 flex-shrink-0" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
              <a
                href={SMS_LINK}
                className="btn-primary mt-6 w-full py-3 text-lg flex items-center justify-center gap-2"
                data-testid="hero-card-text-photo"
              >
                <MessageSquare className="w-4 h-4" strokeWidth={2.5} /> TEXT US A PHOTO
              </a>
            </div>
          </div>
        </div>

        {/* Marquee */}
        <div className="relative bg-forest border-y-2 border-forest text-bone py-4 overflow-hidden">
          <div className="marquee-track gap-12 px-6 font-display tracking-[0.2em] text-xl">
            {Array.from({ length: 2 }).flatMap(() =>
              [
                "TREE TRIMMING",
                "·",
                "DANGEROUS TREE REMOVAL",
                "·",
                "LOT CLEARING",
                "·",
                "DEBRIS HAUL-OFF",
                "·",
                "STORM RESPONSE",
                "·",
                "FREE ESTIMATES",
                "·",
              ].map((t, i) => (
                <span key={`${t}-${i}-${Math.random()}`} className="text-bone/90">
                  {t}
                </span>
              ))
            )}
          </div>
        </div>
      </section>

      {/* ======= SECTIONS BELOW HERO (ordered via flex) ======= */}
      <div className="flex flex-col">

      {/* ======= SERVICES ======= */}
      <section style={{ order: 4 }} id="services" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-20 lg:py-28">
        <div className="grid lg:grid-cols-12 gap-10 mb-12">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.3em] text-safety font-bold">/ 01 — What we do</div>
            <h2 className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.9] mt-4">
              Big Saws.
              <br />
              Bigger Cleanup.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 flex items-end">
            <p className="text-lg text-forest/80 max-w-xl">
              From a single overhanging branch to a forty-acre clear, we bring the rope, the chain,
              the chipper, and the muscle. Same-day estimates, fair quotes, jobs done right the
              first time.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((s, idx) => {
            const Icon = s.icon;
            return (
              <article
                key={s.id}
                data-testid={`service-card-${s.id}`}
                className={`card-brutal p-6 flex flex-col ${
                  s.span ? "sm:col-span-2 lg:col-span-2 lg:row-span-2 bg-forest text-bone hover:bg-forest" : ""
                }`}
                style={s.span ? { background: "#1B2A22" } : {}}
              >
                <div
                  className="w-full aspect-[16/10] bg-cover bg-center border-2 border-forest mb-5"
                  style={{ backgroundImage: `url(${s.image})` }}
                />
                <div className="flex items-center gap-3">
                  <div
                    className={`w-11 h-11 border-2 flex items-center justify-center ${
                      s.span ? "bg-safety border-bone" : "bg-safety border-forest"
                    }`}
                  >
                    <Icon className="w-5 h-5 text-bone" strokeWidth={2.5} />
                  </div>
                  <div className="text-xs uppercase tracking-[0.25em] opacity-70">/ 0{idx + 1}</div>
                </div>
                <h3
                  className={`font-display uppercase tracking-tight mt-3 ${
                    s.span ? "text-4xl sm:text-5xl" : "text-3xl"
                  }`}
                >
                  {s.title}
                </h3>
                <p className={`mt-3 text-base flex-1 ${s.span ? "text-bone/85" : "text-forest/80"}`}>
                  {s.blurb}
                </p>
                <a
                  href="#quote"
                  data-testid={`service-cta-${s.id}`}
                  className={`mt-5 inline-flex items-center gap-2 font-display tracking-widest text-lg ${
                    s.span ? "text-safety" : "text-forest"
                  } hover:gap-3 transition-all`}
                >
                  GET QUOTE <ArrowRight className="w-4 h-4" strokeWidth={3} />
                </a>
              </article>
            );
          })}
        </div>
      </section>

      {/* ======= WHY US ======= */}
      {/* ======= WHY ======= */}
      <section style={{ order: 5 }} id="why" className="bg-forest text-bone border-y-2 border-forest grain">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.3em] text-safety font-bold">/ 02 — Why Acorn</div>
            <h2 className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.9] mt-4">
              Local. Insured.
              <br />
              <span className="text-safety">On Time.</span>
            </h2>
            <p className="mt-6 text-bone/85 text-lg max-w-md">
              We're not a giant national outfit running a dispatch board. We're your neighbors —
              we live here, we work here, and we answer the phone ourselves.
            </p>
            <a
              href={`tel:${PHONE_TEL}`}
              data-testid="why-call-button"
              className="btn-primary mt-8 inline-flex items-center gap-3 px-6 py-4 text-xl"
            >
              <Phone className="w-5 h-5" strokeWidth={3} /> CALL {PHONE_DISPLAY}
            </a>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            {[
              {
                icon: ShieldCheck,
                title: "Fully Insured",
                body: "General liability + workers' comp. Your property is protected before the chainsaw starts.",
              },
              {
                icon: Clock4,
                title: "24/7 Storm Response",
                body: "Tree on the house at midnight? Call. We'll roll a crew as fast as we can safely get there.",
              },
              {
                icon: CheckCircle2,
                title: "Free On-Site Estimates",
                body: "We come out, we look, we tell you the price. No fine print, no surprises.",
              },
              {
                icon: MapPin,
                title: "Right-Here Local",
                body: "Kingston-based. We know your roads, your trees, and the weather that takes 'em down.",
              },
            ].map(({ icon: Icon, title, body }, i) => (
              <div
                key={title}
                data-testid={`why-card-${i}`}
                className="border-2 border-bone p-6 bg-forest hover:bg-black/30 transition-colors"
              >
                <Icon className="w-7 h-7 text-safety" strokeWidth={2.5} />
                <h3 className="font-display text-3xl mt-4 uppercase tracking-tight">{title}</h3>
                <p className="mt-2 text-bone/80">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======= GALLERY (only renders if photos exist) ======= */}
      <div style={{ order: 6 }}>
        <Gallery />
      </div>

      {/* ======= SERVICE AREA ======= */}
      <section style={{ order: 2 }} id="area" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-20 lg:py-28">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.3em] text-safety font-bold">/ 03 — Service area</div>
            <h2 className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.9] mt-4">
              100 Miles
              <br />
              From Kingston.
            </h2>
            <p className="mt-6 text-forest/80 text-lg max-w-md">
              Headquartered in Kingston, TN — we cover Roane, Anderson, Knox, Loudon, McMinn,
              Hamilton, Cumberland and surrounding counties, all the way down to Chattanooga.
              Not sure if you're in range? Just call and ask.
            </p>
            <div className="mt-6 flex items-center gap-3 border-2 border-forest p-4 hard-shadow bg-white">
              <MapPin className="w-6 h-6 text-safety" strokeWidth={2.5} />
              <div>
                <div className="font-display text-2xl tracking-tight">KINGSTON, TN</div>
                <div className="text-xs uppercase tracking-[0.25em] text-forest/70">Home base</div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {SERVICE_AREAS.map((town, i) => (
                <div
                  key={town}
                  data-testid={`area-${town.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`border-2 border-forest p-4 font-display text-2xl tracking-tight uppercase flex items-center justify-between transition-all hover:bg-forest hover:text-bone ${
                    i === 0 ? "bg-safety text-white" : "bg-white"
                  }`}
                >
                  <span>{town}</span>
                  <span className="text-xs tracking-[0.25em] opacity-60">TN</span>
                </div>
              ))}
            </div>
            <p className="mt-5 text-sm uppercase tracking-[0.2em] text-forest/70">
              + Anywhere within 100 miles of Kingston, TN
            </p>
          </div>
        </div>
      </section>

      {/* ======= TESTIMONIALS ======= */}
      <section style={{ order: 7 }} className="bg-bone border-y-2 border-forest">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-20 lg:py-24">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-safety font-bold">/ 04 — Word from neighbors</div>
              <h2 className="font-display uppercase text-5xl sm:text-6xl tracking-tight leading-[0.9] mt-4">
                What Folks
                <br />
                Around Here Say.
              </h2>
            </div>
            <div className="flex items-center gap-1 text-safety">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-safety" strokeWidth={2} />
              ))}
              <span className="ml-2 font-display text-2xl text-forest tracking-tight">5.0</span>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <figure
                key={t.name}
                data-testid={`testimonial-${i}`}
                className="card-brutal p-6 flex flex-col"
              >
                <div className="flex items-center gap-1 text-safety mb-4">
                  {Array.from({ length: 5 }).map((_, j) => (
                    <Star key={j} className="w-4 h-4 fill-safety" strokeWidth={2} />
                  ))}
                </div>
                <blockquote className="text-forest/90 text-base leading-relaxed flex-1">
                  &ldquo;{t.text}&rdquo;
                </blockquote>
                <figcaption className="mt-5 pt-5 border-t-2 border-forest/20">
                  <div className="font-display text-2xl uppercase tracking-tight">{t.name}</div>
                  <div className="text-xs uppercase tracking-[0.25em] text-forest/60">{t.town}</div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* ======= QUOTE FORM ======= */}
      <section style={{ order: 1 }} id="quote" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-20 lg:py-28">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.3em] text-safety font-bold">/ 05 — Free Estimate</div>
            <h2 className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.9] mt-4">
              Get Your
              <br />
              <span className="text-safety">Free Quote.</span>
            </h2>
            <p className="mt-6 text-forest/80 text-lg max-w-md">
              Tell us what's going on with your trees. We'll call you back today (usually within
              the hour) to set up a free, no-pressure on-site estimate.
            </p>

            <div className="mt-10 space-y-4">
              <a
                href={`tel:${PHONE_TEL}`}
                data-testid="quote-side-call"
                className="flex items-center gap-4 border-2 border-forest bg-white p-5 hard-shadow hover:hard-shadow-orange transition-all group"
              >
                <div className="w-12 h-12 bg-safety border-2 border-forest flex items-center justify-center">
                  <Phone className="w-5 h-5 text-bone" strokeWidth={3} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.25em] text-forest/70 font-bold">Call now</div>
                  <div className="font-display text-3xl tracking-tight group-hover:text-safety transition-colors">
                    {PHONE_DISPLAY}
                  </div>
                </div>
              </a>
              <div className="flex items-center gap-4 border-2 border-forest bg-white p-5">
                <div className="w-12 h-12 bg-forest border-2 border-forest flex items-center justify-center">
                  <Clock4 className="w-5 h-5 text-bone" strokeWidth={2.5} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.25em] text-forest/70 font-bold">Hours</div>
                  <div className="font-display text-2xl tracking-tight">Mon–Sat · 7am–7pm</div>
                  <div className="text-sm text-forest/70">Emergency calls 24/7</div>
                </div>
              </div>
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            data-testid="quote-form"
            className="lg:col-span-7 bg-white border-2 border-forest hard-shadow-lg p-6 sm:p-8"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="label-brutal" htmlFor="q-name">Your Name *</label>
                <input
                  id="q-name"
                  data-testid="quote-input-name"
                  className="input-brutal"
                  placeholder="John Smith"
                  value={form.name}
                  onChange={update("name")}
                  required
                />
              </div>
              <div>
                <label className="label-brutal" htmlFor="q-phone">Phone Number *</label>
                <input
                  id="q-phone"
                  data-testid="quote-input-phone"
                  type="tel"
                  className="input-brutal"
                  placeholder="(865) 555-1234"
                  value={form.phone}
                  onChange={update("phone")}
                  required
                />
              </div>
              <div>
                <label className="label-brutal" htmlFor="q-email">Email (optional)</label>
                <input
                  id="q-email"
                  data-testid="quote-input-email"
                  type="email"
                  className="input-brutal"
                  placeholder="you@email.com"
                  value={form.email}
                  onChange={update("email")}
                />
              </div>
              <div>
                <label className="label-brutal" htmlFor="q-address">Property Address *</label>
                <input
                  id="q-address"
                  data-testid="quote-input-address"
                  className="input-brutal"
                  placeholder="123 Oak St, Kingston, TN"
                  value={form.address}
                  onChange={update("address")}
                  required
                />
              </div>
              <div className="sm:col-span-2">
                <label className="label-brutal" htmlFor="q-service">What do you need?</label>
                <select
                  id="q-service"
                  data-testid="quote-input-service"
                  className="input-brutal appearance-none cursor-pointer"
                  value={form.service_type}
                  onChange={update("service_type")}
                >
                  {[
                    "Tree Trimming",
                    "Tree Removal",
                    "Dangerous Tree Removal",
                    "Lot Clearing",
                    "Debris Removal",
                    "Emergency / Storm Damage",
                    "Not Sure — Just Need a Look",
                  ].map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="label-brutal" htmlFor="q-desc">Tell us about the job</label>
                <textarea
                  id="q-desc"
                  data-testid="quote-input-description"
                  className="input-brutal min-h-[120px] resize-y"
                  placeholder="Big oak leaning over the back of the house after the storm. Maybe 60ft tall. Power lines about 20ft away..."
                  value={form.description}
                  onChange={update("description")}
                />
              </div>
            </div>
            <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
              <p className="text-xs uppercase tracking-[0.2em] text-forest/70">
                * Required. We'll never share your info.
              </p>
              <button
                type="submit"
                disabled={submitting}
                data-testid="quote-submit-button"
                className="btn-primary px-7 py-4 text-xl flex items-center justify-center gap-3 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" /> SENDING...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" strokeWidth={3} /> REQUEST FREE QUOTE
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </section>

      </div>
      {/* ======= /SECTIONS BELOW HERO ======= */}

      {/* ======= FOOTER ======= */}
      <footer className="bg-forest text-bone border-t-2 border-forest">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-14 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="flex items-center gap-4">
              <img
                src="/acorn-logo.jpg"
                alt="Acorn Tree Service²"
                className="h-20 w-auto bg-bone border-2 border-bone"
              />
              <div>
                <div className="font-display text-3xl tracking-wider">ACORN TREE SERVICE<sup className="text-safety text-base ml-0.5">2</sup></div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-bone/60">Est. Kingston, TN</div>
              </div>
            </div>
            <p className="mt-5 text-bone/75 max-w-sm">
              Tree trimming, removal, lot clearing, and debris haul-off across East Tennessee.
              Dangerous tree, ugly tree — let us take care of it for you.
            </p>
          </div>

          <div className="md:col-span-3">
            <div className="text-xs uppercase tracking-[0.3em] text-safety font-bold mb-4">Contact</div>
            <a
              href={`tel:${PHONE_TEL}`}
              data-testid="footer-phone"
              className="flex items-center gap-2 hover:text-safety transition-colors mb-2"
            >
              <Phone className="w-4 h-4" /> <span className="font-display text-xl tracking-tight">{PHONE_DISPLAY}</span>
            </a>
            <div className="flex items-start gap-2 text-bone/80 text-sm mt-3">
              <MapPin className="w-4 h-4 mt-0.5" /> Kingston, TN — Serving 100 miles around
            </div>
            <div className="flex items-start gap-2 text-bone/80 text-sm mt-2">
              <Clock4 className="w-4 h-4 mt-0.5" /> Mon–Sat 7am–7pm · 24/7 Emergency
            </div>
          </div>

          <div className="md:col-span-4">
            <div className="text-xs uppercase tracking-[0.3em] text-safety font-bold mb-4">Services</div>
            <ul className="space-y-2 text-bone/80">
              <li>Tree Trimming & Pruning</li>
              <li>Tree Removal</li>
              <li>Dangerous Tree Removal</li>
              <li>Lot Clearing</li>
              <li>Debris & Storm Cleanup</li>
            </ul>
          </div>
        </div>
        <div className="border-t-2 border-bone/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-5 flex flex-col sm:flex-row gap-3 justify-between text-xs uppercase tracking-[0.25em] text-bone/60">
            <div>© {new Date().getFullYear()} Acorn Tree Service² · Kingston, TN</div>
            <div>Licensed · Insured · Locally Owned</div>
          </div>
        </div>
      </footer>

      {/* ======= FLOATING CONTACT BUTTONS (mobile) ======= */}
      <div className="fixed bottom-20 right-5 z-50 md:hidden flex flex-col gap-3 items-end">
        <a
          href={SMS_LINK}
          data-testid="floating-text-button"
          className="btn-dark px-4 py-3 flex items-center gap-2 text-base"
          aria-label="Text Acorn Tree Service a photo"
          style={{ background: "#1B2A22", color: "#F4F1ED" }}
        >
          <MessageSquare className="w-5 h-5" strokeWidth={3} />
          TEXT PHOTO
        </a>
        <a
          href={`tel:${PHONE_TEL}`}
          data-testid="floating-call-button"
          className="btn-primary pulse-ring px-5 py-4 flex items-center gap-3 text-lg"
          aria-label="Call Acorn Tree Service"
        >
          <Phone className="w-5 h-5" strokeWidth={3} />
          CALL NOW
        </a>
      </div>
    </div>
  );
}
